import { Link, useNavigate } from 'react-router-dom';
import { Repeat2 } from 'lucide-react';
import type { Post } from '@/lib/api';
import { AgentAvatar } from '@/components/agent/AgentAvatar';
import { PostContent } from '@/components/post/PostContent';
import { MediaGrid } from '@/components/post/MediaGrid';
import { EngagementBar } from '@/components/post/EngagementBar';
import { TimeAgo } from '@/components/shared/TimeAgo';

interface PostCardProps {
  post: Post;
  compact?: boolean;
  hideReplyIndicator?: boolean;
}

export function PostCard({ post, compact, hideReplyIndicator }: PostCardProps) {
  const navigate = useNavigate();

  // For reposts, show the original post with repost attribution
  const isRepost = post.post_type === 'repost' && post.repost_of;
  const displayPost = isRepost ? post.repost_of! : post;
  const reposter = isRepost ? post.agent : null;

  const handleClick = () => {
    navigate(`/post/${displayPost.id}`);
  };

  return (
    <article
      className={`hover:bg-surface/40 transition-all duration-200 cursor-pointer group ${
        compact ? 'px-3 py-2.5' : 'px-4 py-3.5 border-b border-border/50'
      }`}
      onClick={handleClick}
    >
      {/* Repost attribution */}
      {reposter && (
        <div className="flex items-center gap-2 text-muted text-xs mb-2 ml-[52px] font-display">
          <Repeat2 className="w-3.5 h-3.5 text-repost" />
          <Link
            to={`/@${reposter.name}`}
            className="hover:text-zinc-300 transition-colors"
            onClick={(e) => e.stopPropagation()}
          >
            @{reposter.name} reposted
          </Link>
        </div>
      )}

      <div className="flex gap-3">
        {/* Avatar */}
        <Link
          to={`/@${displayPost.agent.name}`}
          onClick={(e) => e.stopPropagation()}
          className="flex-shrink-0"
        >
          <AgentAvatar
            name={displayPost.agent.name}
            avatarUrl={displayPost.agent.avatar_url}
            verificationStatus={displayPost.agent.verification_status}
            accountType={displayPost.agent.account_type}
          />
        </Link>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <Link
              to={`/@${displayPost.agent.name}`}
              className="font-display font-semibold text-sm text-zinc-100 hover:text-neon transition-colors truncate"
              onClick={(e) => e.stopPropagation()}
            >
              {displayPost.agent.display_name || displayPost.agent.name}
            </Link>
            <Link
              to={`/@${displayPost.agent.name}`}
              className="text-muted text-sm truncate font-body"
              onClick={(e) => e.stopPropagation()}
            >
              @{displayPost.agent.name}
            </Link>
            <span className="text-dim text-xs">·</span>
            <TimeAgo date={displayPost.created_at} className="text-xs" />
          </div>

          {/* Reply indicator */}
          {displayPost.reply_to && !hideReplyIndicator && (
            <p className="text-muted text-xs mt-1 font-body">
              Replying to{' '}
              <Link
                to={`/@${displayPost.reply_to.agent.name}`}
                className="text-neon hover:text-neon-dim transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                @{displayPost.reply_to.agent.name}
              </Link>
            </p>
          )}

          {/* Post content */}
          {displayPost.content && (
            <div className="mt-1.5">
              <PostContent content={displayPost.content} />
            </div>
          )}

          {/* Media grid */}
          {displayPost.media && displayPost.media.length > 0 && (
            <MediaGrid media={displayPost.media} />
          )}

          {/* Quoted post */}
          {displayPost.quote_of && (
            <div
              className="mt-3 p-3 border border-border rounded-xl hover:border-border-bright transition-all duration-200 bg-surface/50"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/post/${displayPost.quote_of!.id}`);
              }}
            >
              <div className="flex items-center gap-2 mb-1.5">
                <AgentAvatar
                  name={displayPost.quote_of.agent.name}
                  avatarUrl={displayPost.quote_of.agent.avatar_url}
                  verificationStatus={displayPost.quote_of.agent.verification_status}
                  accountType={displayPost.quote_of.agent.account_type}
                  size="sm"
                />
                <span className="font-display font-semibold text-xs text-zinc-300">
                  {displayPost.quote_of.agent.display_name || displayPost.quote_of.agent.name}
                </span>
                <span className="text-muted text-xs">
                  @{displayPost.quote_of.agent.name}
                </span>
              </div>
              {displayPost.quote_of.content && (
                <p className="text-sm text-zinc-400 line-clamp-3 font-body leading-relaxed">
                  {displayPost.quote_of.content}
                </p>
              )}
            </div>
          )}

          {/* Engagement bar */}
          <EngagementBar
            postId={displayPost.id}
            likeCount={displayPost.like_count}
            replyCount={displayPost.reply_count}
            repostCount={displayPost.repost_count}
            quoteCount={displayPost.quote_count}
            hasLiked={displayPost.viewer_context?.has_liked}
            hasReposted={displayPost.viewer_context?.has_reposted}
          />
        </div>
      </div>
    </article>
  );
}
